import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Centered pagination control.
 *
 * Props:
 *  - page          current page (1-based)
 *  - totalPages    total number of pages
 *  - onChange(p)   called when the user clicks a page
 *  - testid        base test id (optional)
 */
export default function Pagination({ page, totalPages, onChange, testid = "pagination" }) {
  if (!totalPages || totalPages <= 1) return null;

  const go = (p) => {
    const clamped = Math.max(1, Math.min(totalPages, p));
    if (clamped !== page) onChange(clamped);
  };

  // Build a short page list with ellipsis around current page
  const pages = [];
  const window = 1;
  const add = (p) => { if (!pages.includes(p)) pages.push(p); };
  add(1);
  for (let i = page - window; i <= page + window; i++) {
    if (i > 1 && i < totalPages) add(i);
  }
  add(totalPages);
  pages.sort((a, b) => a - b);

  const buttons = [];
  let prev = 0;
  for (const p of pages) {
    if (p - prev > 1) buttons.push({ type: "gap", key: `gap-${p}` });
    buttons.push({ type: "page", key: p, page: p });
    prev = p;
  }

  const btnBase =
    "h-9 min-w-9 px-3 rounded-full text-sm transition flex items-center justify-center";

  return (
    <div
      data-testid={testid}
      className="flex items-center justify-center gap-1.5 py-5"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <button
        onClick={() => go(1)}
        disabled={page <= 1}
        data-testid={`${testid}-first`}
        className={`${btnBase} text-[#4B5563] hover:bg-[#F5F2EB] disabled:opacity-30 disabled:pointer-events-none`}
        aria-label="Primeira"
      >
        <ChevronsLeft size={15} />
      </button>
      <button
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        data-testid={`${testid}-prev`}
        className={`${btnBase} text-[#4B5563] hover:bg-[#F5F2EB] disabled:opacity-30 disabled:pointer-events-none`}
        aria-label="Anterior"
      >
        <ChevronLeft size={15} />
      </button>

      {buttons.map((b) =>
        b.type === "gap" ? (
          <span key={b.key} className="px-1 text-[#978D78] text-sm select-none">…</span>
        ) : (
          <button
            key={b.key}
            onClick={() => go(b.page)}
            data-testid={`${testid}-page-${b.page}`}
            className={`${btnBase} ${
              b.page === page
                ? "bg-[#0F1829] text-white shadow-[2px_2px_12px_rgba(15,24,41,0.15)]"
                : "text-[#4B5563] hover:bg-[#F5F2EB]"
            }`}
          >
            {b.page}
          </button>
        )
      )}

      <button
        onClick={() => go(page + 1)}
        disabled={page >= totalPages}
        data-testid={`${testid}-next`}
        className={`${btnBase} text-[#4B5563] hover:bg-[#F5F2EB] disabled:opacity-30 disabled:pointer-events-none`}
        aria-label="Próxima"
      >
        <ChevronRight size={15} />
      </button>
      <button
        onClick={() => go(totalPages)}
        disabled={page >= totalPages}
        data-testid={`${testid}-last`}
        className={`${btnBase} text-[#4B5563] hover:bg-[#F5F2EB] disabled:opacity-30 disabled:pointer-events-none`}
        aria-label="Última"
      >
        <ChevronsRight size={15} />
      </button>
    </div>
  );
}
